import {
  CustomFields,
  CustomFieldValue,
  ProviderCustomFields,
} from '../types';
import { CustomField } from '../types/custom-fields/types';

// ... (rest of imports)

// Helper to robustly match Daily-Alert field (handles spacing/underscore/hyphen variants)
export const findDailyAlertField = (
  fields: CustomField[] | undefined
): CustomField | undefined => {
  if (!fields || fields.length === 0) return undefined;
  const normalizeKey = (value?: string) =>
    (value || '')
      .toLowerCase()
      .replace(/[\s_-]+/g, '')
      .trim();
  const matches = ['dailyalert'];
  return fields.find((field) => {
    const nameKey = normalizeKey(field.name);
    const typeKey = normalizeKey(field.field_type);
    if (matches.includes(nameKey) || matches.includes(typeKey)) return true;
    // Loose contains check as fallback
    return (
      nameKey.includes('daily') && nameKey.includes('alert')
    ) || (typeKey.includes('daily') && typeKey.includes('alert'));
  });
};

// Helper to robustly match Office-Notes field
export const findOfficeNotesField = (
  fields: CustomField[] | undefined
): CustomField | undefined => {
  if (!fields || fields.length === 0) return undefined;
  const normalizeKey = (value?: string) =>
    (value || '')
      .toLowerCase()
      .replace(/[\s_-]+/g, '')
      .trim();

  const matches = ['officenotes'];
  return fields.find((field) => {
    const nameKey = normalizeKey(field.name);
    const typeKey = normalizeKey(field.field_type);
    if (matches.includes(nameKey) || matches.includes(typeKey)) return true;
    // Fallback: loose contains check
    return (
      nameKey.includes('office') && nameKey.includes('note')
    ) || (
      typeKey.includes('office') && typeKey.includes('note')
    );
  });
};

// Define interface for cache entries
interface CacheEntry {
  timestamp: number;
  data: unknown[] | Record<string, unknown>;
}

// Extend Window interface to include our custom property
declare global {
  interface Window {
    __customFieldsCache?: Record<string, CacheEntry>;
  }
}

// // Define type guard to check if customFields is of type CustomFields
// const isPracticeCustomFields = (
//   customFields: CustomFields | ProviderCustomFields | undefined
// ): customFields is CustomFields => {
//   return !!customFields && 'Phone' in customFields; // Check for a key unique to CustomFields
// };

// Get the value of a specific custom field
export const getCustomFieldValue = (
  customFields: CustomFields | ProviderCustomFields | undefined,
  fieldName: string
): string => {
  if (!customFields || !fieldName) return '';

  const fieldValues = customFields[fieldName] as
    | CustomFieldValue[]
    | undefined;
  if (!fieldValues || fieldValues.length === 0) return '';

  // Always return the first element [0] for consistency
  // This prevents corrupt duplicate values from being displayed
  const raw = fieldValues[0]?.value;
  const normalized = normalizeCustomFieldValue(String(raw ?? ''));
  return normalized;
};

/**
 * Returns true if the provider is a physician (MD/DO).
 * Checks Credentials custom field first (primary source); falls back to suffix for legacy data.
 */
export const isPhysician = (
  provider:
    | {
        suffix?: string | null;
        customFields?:
          | CustomFields
          | ProviderCustomFields
          | Record<string, unknown>;
      }
    | undefined
): boolean => {
  if (!provider) return false;
  const credentials = getCustomFieldValue(
    provider.customFields as CustomFields | ProviderCustomFields | undefined,
    'Credentials'
  );
  const creds = credentials.toUpperCase();
  if (/\b(MD|DO)\b/.test(creds)) return true;
  const suffix = (provider.suffix || '').toUpperCase();
  return suffix === 'MD' || suffix === 'DO';
};

/**
 * Labs Hours and Imaging Hours are Yes / No / Other custom fields. "Other" means
 * the practice describes availability in free text, which lives under the `other`
 * metadata key.
 */
export const LAB_IMAGING_OPTIONS = ['Yes', 'No', 'Other'] as const;

export type LabImagingStatus = (typeof LAB_IMAGING_OPTIONS)[number];

/** Metadata key holding the free text behind an "Other" answer. */
export const LAB_IMAGING_DETAIL_KEY = 'other';

export interface LabImagingValue {
  /** Empty when the practice has not answered yet. */
  status: LabImagingStatus | '';
  /** Free text describing hours; only meaningful when status is 'Other'. */
  detail: string;
}

/** Legacy Labs-Drawn-Hours metadata: per-day ranges under lowercase day keys. */
const LEGACY_LAB_DAYS = [
  ['sunday', 'Sun'],
  ['monday', 'Mon'],
  ['tuesday', 'Tue'],
  ['wednesday', 'Wed'],
  ['thursday', 'Thu'],
  ['friday', 'Fri'],
  ['saturday', 'Sat'],
] as const;

/** Rebuild free text from legacy per-day lab metadata, e.g. "Mon: 8:00 AM to 12:00 PM". */
const legacyLabDetail = (
  metadata: Record<string, unknown> | undefined
): string => {
  if (!metadata) return '';
  return LEGACY_LAB_DAYS.map(([key, label]) => {
    const raw = metadata[key];
    const ranges = (Array.isArray(raw) ? raw : [raw])
      .map((range) => String(range ?? '').trim())
      .filter(Boolean);
    return ranges.length > 0 ? `${label}: ${ranges.join(', ')}` : '';
  })
    .filter(Boolean)
    .join('; ');
};

/**
 * Reads a Yes/No/Other hours field (Labs-Drawn-Hours, Imaging-Available-Hours).
 * Rows written before these fields became enums kept their hours in `value`
 * (imaging) or in per-day metadata (labs); both read back as 'Other' plus detail.
 */
export const getLabImagingValue = (
  customFields: CustomFields | ProviderCustomFields | undefined,
  fieldName: string
): LabImagingValue => {
  const entry = (
    customFields?.[fieldName] as CustomFieldValue[] | undefined
  )?.[0];
  const value = normalizeCustomFieldValue(String(entry?.value ?? ''));
  const metadata = entry?.metadata as Record<string, unknown> | undefined;

  const option = LAB_IMAGING_OPTIONS.find(
    (opt) => opt.toLowerCase() === value.toLowerCase()
  );
  if (option) {
    return {
      status: option,
      detail:
        option === 'Other'
          ? String(metadata?.[LAB_IMAGING_DETAIL_KEY] ?? '').trim()
          : '',
    };
  }

  const legacyDetail = legacyLabDetail(metadata) || value;
  return legacyDetail
    ? { status: 'Other', detail: legacyDetail }
    : { status: '', detail: '' };
};

/** Display text and chip colour for a Yes/No/Other hours field. */
export const getLabImagingDisplay = (
  value: LabImagingValue
): { displayText: string; bgColor: string } => {
  if (value.status === 'Yes') {
    return { displayText: 'Yes', bgColor: 'background.teal100' };
  }
  if (value.status === 'No') {
    return { displayText: 'No', bgColor: 'background.red100' };
  }
  if (value.status === 'Other') {
    return {
      displayText: value.detail || 'Other',
      bgColor: 'background.gray100',
    };
  }
  return { displayText: '-', bgColor: 'background.gray100' };
};

// Check if a specific custom field exists and has a valid value
export const hasCustomField = (
  customFields: CustomFields | ProviderCustomFields | undefined,
  fieldName: string
): boolean => {
  if (!customFields || !fieldName) return false;
  const arr = customFields[fieldName] as
    | CustomFieldValue[]
    | undefined;
  if (!arr || arr.length === 0) return false;

  // Always check the first element [0] for consistency
  const normalized = normalizeCustomFieldValue(
    String(arr[0]?.value ?? '')
  );
  return !!normalized;
};

// Get metadata count or any specific metadata-related key (future extensibility)
export const getMetadataCount = (
  customFields: CustomFields | undefined,
  fieldName: string,
  metadataKey: string
): number => {
  if (!customFields || !fieldName) return 0;
  const field = customFields[fieldName] as
    | CustomFieldValue[]
    | undefined;
  if (!Array.isArray(field) || field.length === 0) return 0;

  // Always check the first element [0] for consistency
  const md = field[0]?.metadata as
    | Record<string, unknown>
    | undefined;
  if (md && Object.prototype.hasOwnProperty.call(md, metadataKey)) {
    const v = md[metadataKey];
    const n =
      typeof v === 'string' ? parseInt(v as string, 10) : Number(v);
    if (!Number.isNaN(n) && Number.isFinite(n)) return n;
  }
  return 0;
};

export function normalizeCustomFieldValue(value: string): string {
  const trimmed = value.trim();
  const lower = trimmed.toLowerCase();

  if (!trimmed || ['nan', 'null', 'undefined'].includes(lower)) {
    return '';
  }

  if (['yes', 'true'].includes(lower)) {
    return 'Yes';
  }

  if (['no', 'false'].includes(lower)) {
    return 'No';
  }

  return trimmed;
}

const PROVIDER_CUSTOM_FIELD_LABEL_OVERRIDES: Record<string, string> = {
  'HMO-LT3D-Request': 'New patient with HMO request for < 3 days',
  'Supervision-Physician': 'Supervising Physicians',
  'Scheduling-Notes': 'Scheduling Notes',
  'Same-Day': 'Needs to be seen today',
  'Hospital-Backline': 'Hospital Backline Number',
  'Cross-Coverage-Sick': 'Cross Coverage for Sick in Clinic',
  'Cross-Coverage-for-Sick-Market': 'Cross Coverage for Sick in Market',
  'Closest-Urgent-Care': 'Closest Urgent Care',
  'Cross-Schedule-Hospital-Follow-Up':
    'Instructions for Cross-Scheduling Hospital Follow-Ups',
};

export const getProviderCustomFieldLabel = (
  fieldName: string,
  overrides: Record<string, string> = {}
): string => {
  if (!fieldName) return '';
  if (overrides[fieldName]) {
    return overrides[fieldName];
  }
  if (PROVIDER_CUSTOM_FIELD_LABEL_OVERRIDES[fieldName]) {
    return PROVIDER_CUSTOM_FIELD_LABEL_OVERRIDES[fieldName];
  }
  return fieldName.replace(/-/g, ' ');
};

export const getProviderFieldPlaceholderLabel = (
  label: string
): string => {
  if (!label) return '';

  return label
    .split(' ')
    .map((word) => {
      // Keep acronyms (e.g., HMO, ADHD, ID) uppercase in placeholders.
      if (/^[A-Z0-9]{2,}$/.test(word)) {
        return word;
      }
      return word.toLowerCase();
    })
    .join(' ');
};

// Utility function to clear custom fields cache
export const clearCustomFieldsCache = () => {
  if (window.__customFieldsCache) {
    window.__customFieldsCache = {};
  }

  // Clear localStorage cache
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith('chs_cf_cache_')) {
      localStorage.removeItem(key);
    }
  });
};

// Utility function to get cache info for debugging
export const getCustomFieldsCacheInfo = () => {
  const cache = window.__customFieldsCache;
  if (!cache) return { cacheSize: 0, entries: [] };

  const entries = Object.entries(cache).map(
    ([key, value]: [string, CacheEntry]) => ({
      key,
      timestamp: value.timestamp,
      dataLength: value.data?.length || 0,
      age: Date.now() - value.timestamp,
    })
  );

  return {
    cacheSize: Object.keys(cache).length,
    entries,
  };
};

// Utility function to get client ID with fallback
export const getClientIdWithFallback = (): string => {
  const fallbackClientId = import.meta.env.VITE_CLIENT_ID_PRACTICE;
  if (!fallbackClientId) {
    console.warn('VITE_CLIENT_ID_PRACTICE not set');
    return '';
  }

  return fallbackClientId;
};

// Utility function to ensure client ID is available
export const ensureClientIdAvailable = async (): Promise<string> => {
  return getClientIdWithFallback();
};

// Utility function to format provider name consistently
// Format: "First name last name, suffix, credentials"
// Note: Nickname is handled separately in ProviderName component with italics styling
export const formatProviderName = (
  provider: {
    firstname?: string;
    firstName?: string;
    lastname?: string;
    lastName?: string;
    middlename?: string | null;
    middleName?: string | null;
    suffix?: string | null;
    title?: string | null;
    nickname?: string | null;
    customFields?: CustomFields | ProviderCustomFields | Record<string, unknown>;
  },
  includeCredentials: boolean = true
): string => {
  const firstName = provider?.firstname || provider?.firstName || '';
  const lastName = provider?.lastname || provider?.lastName || '';
  const middleName =
    provider?.middlename || provider?.middleName || '';
  const suffix = provider?.suffix || '';
  const title = provider?.title || '';

  // Build name parts
  const nameParts = [];

  // Add title at the beginning if present
  if (title && title.trim() !== '') {
    nameParts.push(title);
  }
  if (firstName) nameParts.push(firstName);
  if (middleName) nameParts.push(middleName);
  if (lastName) nameParts.push(lastName);

  const fullName = nameParts.join(' ');

  // Add suffix with comma if present
  let nameWithSuffix = suffix ? `${fullName}, ${suffix}` : fullName;

  // Add credentials with comma if present and includeCredentials is true
  if (includeCredentials && provider?.customFields) {
    // Type assertion to handle Record<string, unknown> case
    const credentials = getCustomFieldValue(
      provider.customFields as CustomFields | ProviderCustomFields,
      'Credentials'
    );
    if (credentials && credentials.trim() !== '') {
      nameWithSuffix = `${nameWithSuffix}, ${credentials}`;
    }
  }

  return nameWithSuffix;
};
